import axios from 'axios'
import { useEffect, useState } from 'react'
import { SERVER_URL } from '@/constants/api'

/**
 * Server Health Check Hook
 *
 * This hook handles the server wake-up mechanism by:
 * 1. Checking if the server is running by hitting the /health endpoint
 * 2. Retrying with exponential backoff if the server is not responding
 * 3. Providing different retry counts for development vs production
 * 4. Handling both development (server not started) and production (server sleeping) scenarios
 *
 * In development: Retries 15 times (30+ seconds) to allow time for manual server startup
 * In production: Retries 10 times (20+ seconds) to wake up sleeping servers
 */

interface ServerHealthStatus {
  isServerReady: boolean
  isChecking: boolean
  error: string | null
  retryCount: number
}

const MAX_RETRIES = import.meta.env.DEV ? 15 : 10 // More retries in development
const RETRY_DELAY = 2000 // 2 seconds
const HEALTH_CHECK_TIMEOUT = 5000 // 5 seconds

export function useServerHealthCheck() {
  const [status, setStatus] = useState<ServerHealthStatus>({
    isServerReady: false,
    isChecking: true,
    error: null,
    retryCount: 0,
  })

  const checkServerHealth = async (retryCount = 0): Promise<boolean> => {
    try {
      setStatus((prev) => ({
        ...prev,
        isChecking: true,
        retryCount,
        error: null,
      }))

      // Create a separate axios instance for health check to avoid interceptors
      const healthAxios = axios.create({
        timeout: HEALTH_CHECK_TIMEOUT,
        withCredentials: false,
      })

      // Try to hit a simple health check endpoint
      const endpoint = `${SERVER_URL}/health`
      const response = await healthAxios.get(endpoint, {
        headers: {
          'Cache-Control': 'no-cache',
        },
      })

      if (response.status === 200) {
        setStatus((prev) => ({
          ...prev,
          isServerReady: true,
          isChecking: false,
          error: null,
        }))
        return true
      }

      throw new Error('Server not ready')
    } catch (error) {
      console.log(
        `Server health check attempt ${retryCount + 1} failed:`,
        error,
      )

      // Determine error type for better user feedback
      const isNetworkError =
        axios.isAxiosError(error) &&
        (error.code === 'ECONNREFUSED' ||
          error.code === 'ERR_NETWORK' ||
          error.message.includes('Network Error') ||
          !error.response)

      if (retryCount < MAX_RETRIES) {
        setStatus((prev) => ({
          ...prev,
          retryCount: retryCount + 1,
          error: isNetworkError
            ? `Connecting to server... (attempt ${retryCount + 1}/${MAX_RETRIES})`
            : `Waiting for server... (attempt ${retryCount + 1}/${MAX_RETRIES})`,
        }))

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY))
        return checkServerHealth(retryCount + 1)
      } else {
        const errorMessage = isNetworkError
          ? 'Unable to connect to the server. Please check if the server is running.'
          : 'Server is not responding properly. Please try again later.'

        setStatus((prev) => ({
          ...prev,
          isChecking: false,
          error: errorMessage,
        }))
        return false
      }
    }
  }

  const retryHealthCheck = () => {
    setStatus((prev) => ({
      ...prev,
      retryCount: 0,
      error: null,
    }))
    checkServerHealth(0)
  }

  useEffect(() => {
    checkServerHealth(0)
  }, [])

  return {
    ...status,
    retryHealthCheck,
  }
}
